import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SEOHead = ({
    title,
    description,
    image,
    type = 'website',
    publishedTime,
    modifiedTime,
    author,
    schema,
    breadcrumbs,
    noindex = false
}) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    // Base URL
    const baseUrl = 'https://gymtracker.pro';

    // Construct current URL path
    const currentPath = location.pathname;

    // Get language from URL query parameter
    const searchParams = new URLSearchParams(location.search);
    const currentLanguage = searchParams.get('lng') || i18n.language;

    // Supported languages
    const languages = ['es', 'en', 'fr', 'de', 'it', 'pt'];

    // Default values if props are not provided
    const defaultTitle = "GymTracker Pro";
    const defaultDescription = "Gestiona tus rutinas, registra tus progresos y alcanza tus objetivos con la app definitiva para entrenadores y atletas.";
    const defaultImage = `${baseUrl}/og-image.png`;

    const finalTitle = title ? (title.includes('GymTracker Pro') ? title : `${title} | GymTracker Pro`) : defaultTitle;
    const finalDescription = description || defaultDescription;
    const finalImage = image ? (image.startsWith('http') ? image : `${baseUrl}${image.startsWith('/') ? '' : '/'}${image}`) : defaultImage;

    // Canonical URL should always be clean (without query params)
    // This prevents duplicate content issues with ?lng= parameter
    const canonicalUrl = `${baseUrl}${currentPath}`;

    // Keywords for SEO
    const keywords = "GymTracker, Gym Tracker, Workout Log, Rutinas Gym, Fitness App, GymTracker Pro, Entrenador Personal, Coach IA, Macro Calculator, Calculadora de Macros, Anatomy 3D, Anatomía 3D, 1RM";

    // Final URL for sharing/OG tags can include language if present
    const finalUrl = searchParams.has('lng')
        ? `${baseUrl}${currentPath}?lng=${currentLanguage}`
        : `${baseUrl}${currentPath}`;

    // Schema.org WebSite Data
    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "GymTracker Pro",
        "url": baseUrl,
        "description": finalDescription,
        "inLanguage": currentLanguage,
        "potentialAction": {
            "@type": "SearchAction",
            "target": `${baseUrl}/tools?q={search_term_string}`,
            "query-input": "required name=search_term_string"
        }
    };

    // Schema.org Organization Data
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "GymTracker Pro",
        "url": baseUrl,
        "logo": `${baseUrl}/pwa-512x512.png`,
        "sameAs": [
            "https://twitter.com/gymtrackerpro",
            "https://facebook.com/gymtrackerpro",
            "https://instagram.com/gymtrackerpro"
        ],
        "contactPoint": {
            "@type": "ContactPoint",
            "email": "info@gymtracker.pro",
            "contactType": "customer support"
        }
    };

    // Schema.org SoftwareApplication for PWA & Play Store
    const softwareAppSchema = {
        "@context": "https://schema.org",
        "@type": ["SoftwareApplication", "MobileApplication"],
        "name": "GymTracker Pro",
        "alternateName": "GymTrackerPro",
        "operatingSystem": "Android, Web",
        "applicationCategory": "HealthApplication",
        "applicationSubCategory": "Fitness",
        "downloadUrl": "https://play.google.com/store/apps/details?id=gymtracker.pro",
        "installUrl": "https://play.google.com/store/apps/details?id=gymtracker.pro",
        "url": baseUrl,
        "image": `${baseUrl}/pwa-512x512.png`,
        "screenshot": `${baseUrl}/og-image.png`,
        "author": {
            "@type": "Person",
            "name": "Francisco Jesús Lara López",
            "url": "https://play.google.com/store/apps/developer?id=Francisco+Jes%C3%BAs+Lara+L%C3%B3pez"
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
        },
        "featureList": "Workout Tracking, 1RM Calculator, Macro Calculator, 3D Anatomy, AI Coach, Plate Calculator, IPF Points Calculator",
        "softwareVersion": "1.0.8",
        "releaseNotes": "Latest version with improved performance and new features",
        "contentRating": "Everyone",
        "inLanguage": ["es", "en", "fr", "de", "it", "pt", "pl"],
        "description": finalDescription
    };

    // BreadcrumbList Schema
    const breadcrumbSchema = breadcrumbs ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, idx) => ({
            "@type": "ListItem",
            "position": idx + 1,
            "name": crumb.name,
            "item": crumb.item ? (crumb.item.startsWith('http') ? crumb.item : `${baseUrl}${crumb.item}`) : baseUrl
        }))
    } : null;

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{finalTitle}</title>
            <meta name="title" content={finalTitle} />
            <meta name="description" content={finalDescription} />

            {/* Performance Preloads & Preconnects */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="preconnect" href="https://i.ytimg.com" />
            <link rel="preconnect" href="https://www.youtube.com" />

            {/* Indexing */}
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Canonical Link - Always clean URL without ?lng= */}
            <link rel="canonical" href={canonicalUrl} />

            {/* Hreflang Tags */}
            {languages.map((lng) => (
                <link
                    key={lng}
                    rel="alternate"
                    hreflang={lng}
                    href={`${baseUrl}${currentPath}?lng=${lng}`}
                />
            ))}
            {/* x-default points to clean URL */}
            <link rel="alternate" hreflang="x-default" href={canonicalUrl} />

            {/* Keywords */}
            <meta name="keywords" content={keywords} />

            {/* Open Graph / Facebook / LinkedIn */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={finalUrl} />
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:image" content={finalImage} />
            <meta property="og:image:alt" content={`${finalTitle} - Preview`} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content="Gym Tracker Pro" />
            <meta property="og:locale" content={i18n.language === 'es' ? 'es_ES' : 'en_US'} />
            <meta property="og:locale:alternate" content={i18n.language === 'es' ? 'en_US' : 'es_ES'} />
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            {author && <meta property="article:author" content={author} />}

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@GymTrackerPro" />
            <meta name="twitter:creator" content="@GymTrackerPro" />
            <meta name="twitter:url" content={finalUrl} />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={finalImage} />
            <meta name="twitter:image:alt" content={`${finalTitle} - Preview`} />

            {/* Mobile & PWA */}
            <meta name="theme-color" content="#DC2626" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
            <meta name="apple-mobile-web-app-title" content="GymTracker Pro" />

            {/* WebSite Schema */}
            <script type="application/ld+json">
                {JSON.stringify(websiteSchema)}
            </script>

            {/* Organization Schema */}
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>

            {/* SoftwareApplication Schema */}
            <script type="application/ld+json">
                {JSON.stringify(softwareAppSchema)}
            </script>

            {/* Breadcrumb Schema */}
            {breadcrumbSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            )}

            {/* Custom Page Schema */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(Array.isArray(schema) ? schema : [schema])}
                </script>
            )}

            <meta name="theme-color" content="#000000" />
        </Helmet>
    );
};

export default SEOHead;
